import { useMutation } from '@apollo/client/react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormHelperText,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { Loading } from 'react-admin';

import {
  AdminCreateDomainDocument,
  AdminCreateOfficeDocument,
  type AdminLaunchClientSetupFragment,
  type AdminLaunchOfficeFragment,
  type AdminLaunchTestUserFragment,
  AdminLaunchUpdateClientDocument,
  AdminRemoveDomainDocument,
  AdminRemoveOfficeDocument,
  AdminUpdateOfficeDocument,
} from '../../../graphql/types';
import { FieldName } from './FieldNames';
import {
  ChoiceCard,
  ChoiceCards,
  CollapsedSettings,
  parseWholeNumber,
  ReloadAlert,
  Section,
  SettingSwitch,
} from './fields';
import { TestUsers } from './TestUsers';
import type { ClientLaunch, SetupChoices, StepProps } from './types';
import { useStepSetup } from './useStepSetup';

type OfficeRow = {
  /** Stable React key; new rows have no `id` until saved. */
  key: string;
  id?: string;
  clientOfficeId: string;
  name: string;
  isAppEligible: boolean;
  isFcmEligible: boolean;
};

type Source = 'eligFile' | 'eligGym' | 'eligDomainOnly';

const SOURCES: {
  key: Source;
  title: string;
  tag?: { label: string; recommended: boolean };
  description: string;
  fieldName?: string;
}[] = [
  {
    key: 'eligFile',
    title: 'Eligibility file',
    tag: { label: 'Recommended', recommended: true },
    description: 'Client sends a roster over SFTP.',
    fieldName: 'eligible_email_addresses',
  },
  {
    key: 'eligGym',
    title: 'Active gym membership',
    description:
      'Members with an active Mindbody membership get access, including people not in the file.',
    fieldName: 'clients.usesFitnessCenterForEligibility',
  },
  {
    key: 'eligDomainOnly',
    title: 'Email domain only',
    tag: { label: 'Not recommended', recommended: false },
    description:
      'Anyone with a matching work email gets access. No eligibility check.',
    fieldName: 'domains',
  },
];

const toOfficeRow = (office: AdminLaunchOfficeFragment): OfficeRow => ({
  key: office.id,
  id: office.id,
  clientOfficeId: office.clientOfficeId,
  name: office.name ?? '',
  isAppEligible: office.isAppEligible,
  isFcmEligible: office.isFcmEligible,
});

const normalizeDomain = (value: string) =>
  value.trim().toLowerCase().replace(/^@/, '');

const looksLikeDomain = (value: string) =>
  /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(value);

let nextOfficeKey = 0;

type FormProps = StepProps & {
  launch: ClientLaunch;
  client: AdminLaunchClientSetupFragment;
  offices: AdminLaunchOfficeFragment[];
  testUsers: AdminLaunchTestUserFragment[];
  refetch: () => Promise<unknown>;
  /** Set when the latest reload failed; saving waits for a successful one. */
  reloadError: string | undefined;
};

const EligibilityForm: React.FC<FormProps> = ({
  client,
  offices: savedOffices,
  testUsers,
  refetch,
  reloadError,
  readOnly,
  saving,
  choices,
  onChoicesChange,
  onEdit,
  registerSave,
}) => {
  const [sources, setSources] = React.useState<Pick<SetupChoices, Source>>({
    eligFile: choices.eligFile,
    eligGym: choices.eligGym,
    eligDomainOnly: choices.eligDomainOnly,
  });
  const [fileHasOffices, setFileHasOffices] = React.useState(
    choices.fileHasOffices,
  );
  const [serviceType, setServiceType] = React.useState(
    client.usesEligibilityServiceType,
  );
  const [gymNeedsFcmEmail, setGymNeedsFcmEmail] = React.useState(
    client.requiresEligibleEmailForGym,
  );
  const [maxFamilyMembers, setMaxFamilyMembers] = React.useState(
    String(client.maxFamilyMembers),
  );
  const [domains, setDomains] = React.useState(
    client.domains.nodes.map((node) => node.domain),
  );
  const [newDomain, setNewDomain] = React.useState('');
  const [domainError, setDomainError] = React.useState<string>();
  const [offices, setOffices] = React.useState(savedOffices.map(toOfficeRow));
  const [removedOfficeIds, setRemovedOfficeIds] = React.useState<string[]>([]);
  const [showErrors, setShowErrors] = React.useState(false);

  const [updateClient] = useMutation(AdminLaunchUpdateClientDocument);
  const [createDomain] = useMutation(AdminCreateDomainDocument);
  const [removeDomain] = useMutation(AdminRemoveDomainDocument);
  const [createOffice] = useMutation(AdminCreateOfficeDocument);
  const [updateOffice] = useMutation(AdminUpdateOfficeDocument);
  const [removeOffice] = useMutation(AdminRemoveOfficeDocument);

  const usesOffices = sources.eligFile && fileHasOffices;
  const hasContractMappings = client.studios.some(
    (studio) => studio.mboContractMappings.nodes.length > 0,
  );
  const familyMembers = parseWholeNumber(maxFamilyMembers);

  const errors = {
    sources:
      !sources.eligFile && !sources.eligGym && !sources.eligDomainOnly
        ? 'Pick at least one eligibility source.'
        : undefined,
    domains:
      sources.eligDomainOnly && !domains.length
        ? 'Add at least one email domain.'
        : undefined,
    offices:
      usesOffices && !offices.length
        ? 'Add the offices listed in the file.'
        : undefined,
    officeIds:
      usesOffices && offices.some((office) => !office.clientOfficeId.trim())
        ? 'Every office needs an office ID.'
        : undefined,
    family: familyMembers === null ? 'Enter a whole number, or 0.' : undefined,
    // Already on is left alone, as on the client edit page.
    serviceType:
      sources.eligGym &&
      serviceType &&
      hasContractMappings &&
      !client.usesEligibilityServiceType
        ? "Studios with MBO contract mappings can't use a special MBO package."
        : undefined,
  };
  const firstError = Object.values(errors).find(Boolean);

  const edit = (patch?: Partial<SetupChoices>) => {
    if (patch) {
      onChoicesChange?.(patch);
    }
    onEdit();
  };

  // Email domain only can't be combined with the other sources.
  const toggleSource = (key: Source, checked: boolean) => {
    const patch: Partial<Pick<SetupChoices, Source>> = { [key]: checked };
    if (checked && key === 'eligDomainOnly') {
      patch.eligFile = false;
      patch.eligGym = false;
    } else if (checked) {
      patch.eligDomainOnly = false;
    }
    setSources((current) => ({ ...current, ...patch }));
    edit(patch);
  };

  const handleFileHasOffices = (checked: boolean) => {
    setFileHasOffices(checked);
    edit({ fileHasOffices: checked });
  };

  const handleAddDomain = () => {
    const domain = normalizeDomain(newDomain);
    if (!looksLikeDomain(domain)) {
      setDomainError('Enter a domain like company.com.');
      return;
    }
    setDomainError(undefined);
    setNewDomain('');
    if (!domains.includes(domain)) {
      setDomains((current) => [...current, domain]);
      edit();
    }
  };

  const updateOfficeRow = (key: string, patch: Partial<OfficeRow>) => {
    setOffices((current) =>
      current.map((office) =>
        office.key === key ? { ...office, ...patch } : office,
      ),
    );
    edit();
  };

  const handleRemoveOffice = (office: OfficeRow) => {
    setOffices((current) => current.filter((row) => row.key !== office.key));
    const { id } = office;
    if (id) {
      setRemovedOfficeIds((current) => [...current, id]);
    }
    edit();
  };

  React.useEffect(() => {
    if (!registerSave) {
      return;
    }
    registerSave(async () => {
      if (reloadError) {
        throw new Error(
          "The latest eligibility settings didn't load, so they can't be saved. Try loading them again.",
        );
      }
      if (firstError || familyMembers === null) {
        setShowErrors(true);
        throw new Error(firstError);
      }
      try {
        // Domains are what makes "Email domain only" work, so they go when
        // it's turned off.
        const wanted = sources.eligDomainOnly ? domains : [];
        for (const node of client.domains.nodes) {
          if (!wanted.includes(node.domain)) {
            await removeDomain({ variables: { id: node.id } });
          }
        }
        const existing = client.domains.nodes.map((node) => node.domain);
        for (const domain of wanted.filter((d) => !existing.includes(d))) {
          await createDomain({
            variables: { input: { clientId: client.id, domain } },
          });
        }

        // Record each office write as it lands, so a retry after a failure
        // doesn't remove or create the same office twice.
        for (const id of removedOfficeIds) {
          await removeOffice({ variables: { id } });
          setRemovedOfficeIds((current) => current.filter((r) => r !== id));
        }
        if (usesOffices) {
          for (const office of offices) {
            const input = {
              clientOfficeId: office.clientOfficeId.trim(),
              name: office.name.trim() || null,
              isAppEligible: office.isAppEligible,
              isFcmEligible: office.isFcmEligible,
            };
            if (office.id) {
              await updateOffice({
                variables: { input: { id: office.id, ...input } },
              });
            } else {
              const { data } = await createOffice({
                variables: { input: { clientId: client.id, ...input } },
              });
              const id = data?.data.office.id;
              setOffices((current) =>
                current.map((row) =>
                  row.key === office.key ? { ...row, id } : row,
                ),
              );
            }
          }
        }

        // Last, so the client it returns has the new domain count.
        await updateClient({
          variables: {
            input: {
              id: client.id,
              usesFitnessCenterForEligibility: sources.eligGym,
              usesEligibilityServiceType: sources.eligGym && serviceType,
              requiresEligibleEmailForGym: gymNeedsFcmEmail,
              maxFamilyMembers: familyMembers,
            },
          },
        });
      } finally {
        // Also after a failure: the domains a retry compares against come
        // from this refetch.
        await refetch().catch(() => undefined);
      }
    });
    return () => registerSave(undefined);
  }, [
    registerSave,
    reloadError,
    firstError,
    familyMembers,
    sources,
    domains,
    client,
    removedOfficeIds,
    usesOffices,
    offices,
    serviceType,
    gymNeedsFcmEmail,
    refetch,
    updateClient,
    createDomain,
    removeDomain,
    createOffice,
    updateOffice,
    removeOffice,
  ]);

  const optionalInUse = (familyMembers ? 1 : 0) + (gymNeedsFcmEmail ? 1 : 0);

  return (
    <Stack spacing={3}>
      <Alert severity="warning">
        Saving this step takes effect right away. Anyone who matches these
        sources can sign up as soon as it's saved.
      </Alert>

      <Section title="Eligibility sources" hint="Pick one or more.">
        <ChoiceCards>
          {SOURCES.map((source) => (
            <ChoiceCard
              key={source.key}
              checked={sources[source.key]}
              control={
                <Checkbox
                  checked={sources[source.key]}
                  onChange={(event) =>
                    toggleSource(source.key, event.target.checked)
                  }
                />
              }
              title={source.title}
              tag={source.tag}
              description={source.description}
              fieldName={source.fieldName}
            />
          ))}
        </ChoiceCards>
        {showErrors && errors.sources && (
          <FormHelperText error>{errors.sources}</FormHelperText>
        )}
      </Section>

      {sources.eligFile && (
        <Section title="Eligibility file">
          <Typography variant="body2">
            The client uploads to their SFTP folder{' '}
            <Box component="code">fit/{client.id}/</Box>. Each upload replaces
            the current list: people missing from the new file are archived. An
            upload that shrinks the list by more than 6% is held until it's sent
            again with an <Box component="code">override-</Box> filename prefix.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            FCM rosters use{' '}
            <Box component="code">fcm/{'{ssoConnection}'}/eligible.csv</Box>{' '}
            instead.
          </Typography>
          <SettingSwitch
            label="The file includes an office ID column"
            description="Office IDs route people to offices and gym locations. Most files don't have one."
            fieldName="(file)"
            checked={fileHasOffices}
            onChange={handleFileHasOffices}
          />
          {fileHasOffices && (
            <OfficesTable
              offices={offices}
              readOnly={readOnly}
              error={
                showErrors ? (errors.offices ?? errors.officeIds) : undefined
              }
              onAdd={() => {
                nextOfficeKey += 1;
                setOffices((current) => [
                  ...current,
                  {
                    key: `new-${nextOfficeKey}`,
                    clientOfficeId: '',
                    name: '',
                    isAppEligible: true,
                    isFcmEligible: false,
                  },
                ]);
                edit();
              }}
              onChange={updateOfficeRow}
              onRemove={handleRemoveOffice}
            />
          )}
        </Section>
      )}

      {sources.eligGym && (
        <Box>
          <SettingSwitch
            label="Gym members need a special MBO package to get app access"
            fieldName="clients.usesEligibilityServiceType"
            description={
              hasContractMappings
                ? "This client's studios have MBO contract mappings, so it can't be turned on."
                : undefined
            }
            checked={serviceType}
            disabled={hasContractMappings && !serviceType}
            onChange={(checked) => {
              setServiceType(checked);
              edit();
            }}
          />
          {showErrors && errors.serviceType && (
            <FormHelperText error>{errors.serviceType}</FormHelperText>
          )}
        </Box>
      )}

      {sources.eligDomainOnly && (
        <Section
          title="Email domains"
          hint="New sign-ups with these domains join this client."
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {domains.length ? (
              domains.map((domain) => (
                <Chip
                  key={domain}
                  label={domain}
                  // A chip isn't a form control, so the flow's disabled
                  // fieldset doesn't stop a delete during a save.
                  onDelete={
                    readOnly || saving
                      ? undefined
                      : () => {
                          setDomains((current) =>
                            current.filter((d) => d !== domain),
                          );
                          edit();
                        }
                  }
                />
              ))
            ) : (
              <Typography variant="body2" color="text.disabled">
                No domains yet.
              </Typography>
            )}
          </Stack>
          {!readOnly && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="Email domain"
                placeholder="company.com"
                value={newDomain}
                error={Boolean(domainError)}
                helperText={domainError ?? ' '}
                onChange={(event) => setNewDomain(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddDomain();
                  }
                }}
              />
              <Button variant="outlined" onClick={handleAddDomain}>
                Add domain
              </Button>
            </Stack>
          )}
          {showErrors && errors.domains && (
            <FormHelperText error>{errors.domains}</FormHelperText>
          )}
          <FieldName>domains</FieldName>
        </Section>
      )}

      {!sources.eligDomainOnly && (
        <TestUsers
          clientId={client.id}
          eligibleEmails={testUsers}
          readOnly={readOnly}
          onChanged={refetch}
        />
      )}

      <CollapsedSettings
        title="Optional settings"
        description="Family sharing and gym restrictions. Most clients don't use these."
        inUse={optionalInUse}
        inUseLabel="in use"
        noneLabel="None"
        forceOpen={showErrors && Boolean(errors.family)}
      >
        <SettingSwitch
          label="Gym access requires FCM-eligible email"
          description="Only people marked FCM-eligible in the file can join the gym."
          fieldName="clients.requiresEligibleEmailForGym"
          checked={gymNeedsFcmEmail}
          onChange={(checked) => {
            setGymNeedsFcmEmail(checked);
            edit();
          }}
        />
        <Box sx={{ maxWidth: 320 }}>
          <TextField
            size="small"
            fullWidth
            label="Family sharing invitations"
            inputMode="numeric"
            value={maxFamilyMembers}
            error={showErrors && Boolean(errors.family)}
            helperText={
              showErrors && errors.family
                ? errors.family
                : '0 turns family sharing off.'
            }
            onChange={(event) => {
              setMaxFamilyMembers(event.target.value);
              edit();
            }}
          />
          <FieldName>clients.maxFamilyMembers</FieldName>
        </Box>
      </CollapsedSettings>
    </Stack>
  );
};

const OfficesTable: React.FC<{
  offices: OfficeRow[];
  readOnly: boolean;
  error: string | undefined;
  onAdd: () => void;
  onChange: (key: string, patch: Partial<OfficeRow>) => void;
  onRemove: (office: OfficeRow) => void;
}> = ({ offices, readOnly, error, onAdd, onChange, onRemove }) => (
  <Stack spacing={1.5}>
    <Alert severity="info">
      Add every office ID in the file, spelled exactly the same. Save offices
      before the client's first upload: people whose office ID isn't listed are
      skipped, and a file with office IDs fails completely if the client has no
      offices.
    </Alert>
    {offices.length > 0 && (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell>Office ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">App</TableCell>
              <TableCell align="center">FCM</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {offices.map((office, index) => (
              <TableRow key={office.key}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={office.clientOfficeId}
                    error={Boolean(error) && !office.clientOfficeId.trim()}
                    slotProps={{
                      htmlInput: { 'aria-label': `Office ${index + 1} ID` },
                    }}
                    onChange={(event) =>
                      onChange(office.key, {
                        clientOfficeId: event.target.value,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={office.name}
                    slotProps={{
                      htmlInput: { 'aria-label': `Office ${index + 1} name` },
                    }}
                    onChange={(event) =>
                      onChange(office.key, { name: event.target.value })
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={office.isAppEligible}
                    slotProps={{
                      input: {
                        'aria-label': `Office ${index + 1} app eligible`,
                      },
                    }}
                    onChange={(event) =>
                      onChange(office.key, {
                        isAppEligible: event.target.checked,
                      })
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={office.isFcmEligible}
                    slotProps={{
                      input: {
                        'aria-label': `Office ${index + 1} FCM eligible`,
                      },
                    }}
                    onChange={(event) =>
                      onChange(office.key, {
                        isFcmEligible: event.target.checked,
                      })
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label={`Remove office ${index + 1}`}
                    disabled={readOnly}
                    onClick={() => onRemove(office)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
    {error && <FormHelperText error>{error}</FormHelperText>}
    <FieldName>offices</FieldName>
    {!readOnly && (
      <Box>
        <Button size="small" onClick={onAdd}>
          Add office
        </Button>
      </Box>
    )}
  </Stack>
);

/** Who can sign up: eligibility sources, offices, domains and test users. */
export const EligibilityStep: React.FC<StepProps> = (props) => {
  const { launch, registerSave } = props;
  // Saving diffs the form against the client's current domains, so it waits
  // for the server rather than a cached copy.
  const { setup, firstLoad, data, reloadError, retry } = useStepSetup(
    launch?.client.id,
    registerSave,
    'eligibility settings',
  );
  const client = data?.client;

  // Eligibility comes after step 1, so the launch always exists by then.
  if (!launch) {
    return null;
  }
  if (firstLoad) {
    return <Loading />;
  }
  if (!data) {
    return (
      <Alert severity="error">
        {setup.error?.message ?? "The eligibility settings didn't load."}
      </Alert>
    );
  }
  if (!client) {
    return <Alert severity="warning">This client doesn't exist.</Alert>;
  }
  return (
    <Stack spacing={2}>
      {reloadError && (
        <ReloadAlert
          settingsName="eligibility settings"
          message={reloadError}
          onRetry={retry}
        />
      )}
      <EligibilityForm
        {...props}
        launch={launch}
        client={client}
        offices={data.offices.nodes}
        testUsers={data.eligibleEmailAddresses.nodes}
        refetch={setup.refetch}
        reloadError={reloadError}
      />
    </Stack>
  );
};
